// src/layout/builders/Documentupload..jsx
import React, { useState, useEffect } from "react";
import useProjectStatus from "../../store/builders/useProjectStatus";
import { useParams, useNavigate } from "react-router-dom";
import {
  AiOutlineLoading3Quarters,
  AiOutlineFilePdf,
  AiOutlineFileImage,
  AiOutlineFileUnknown,
} from "react-icons/ai";
import { toast } from "react-hot-toast";

// Helper to get file icon
const getFileIcon = (fileName) => {
  if (!fileName) return <AiOutlineFileUnknown className="w-5 h-5 text-gray-500" />;
  const extension = fileName.split(".").pop()?.toLowerCase();
  if (["pdf"].includes(extension)) return <AiOutlineFilePdf className="w-5 h-5 text-red-500" />;
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(extension)) return <AiOutlineFileImage className="w-5 h-5 text-blue-500" />;
  return <AiOutlineFileUnknown className="w-5 h-5 text-gray-500" />;
};

const DocumentUpload = ({ onBack }) => { // Receive onBack prop
  const { id: projectIdFromParams } = useParams();
  const navigate = useNavigate();

  const {
    checkCompletionDocumentsStatus,
    uploadCompletionDocuments,
    isLoading: isStoreLoading,
    // documentStatus, // We fetch it locally now
  } = useProjectStatus();

  const [selectedFiles, setSelectedFiles] = useState({
    specificationReport: null,
    warrantyDocument: null,
    completionCertificate: null,
    siteImage: null,
  });
  const [isCheckingDocs, setIsCheckingDocs] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [apiDocuments, setApiDocuments] = useState([]); // Stores status from API

  // Mapping from API names to state keys (adjust if API names differ)
  const docKeyMapping = {
    "Specification Report": "specificationReport",
    "Warranty Document": "warrantyDocument",
    "Completion Certificate": "completionCertificate",
    "Site Image": "siteImage",
  };

  // Fetch document status when the component mounts or projectId changes
  useEffect(() => {
    const fetchStatus = async () => {
      setIsCheckingDocs(true);
      const statusData = await checkCompletionDocumentsStatus(projectIdFromParams);
      if (statusData && statusData.documents) {
        setApiDocuments(statusData.documents); // Store the detailed status
      } else {
        toast.error("Could not fetch document requirements.");
        if (onBack) onBack();
        else navigate("/builder/liveproject");
      }
      setIsCheckingDocs(false);
    };
    if (projectIdFromParams) {
      fetchStatus();
    }
  }, [projectIdFromParams, checkCompletionDocumentsStatus, navigate, onBack]);

  const handleFileChange = (e, docKey) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5 MB
      if (file.size > maxSize) {
        toast.error(`${getFriendlyName(docKey)} exceeds the 5MB size limit.`);
        e.target.value = null; // Clear the input
        return;
      }
      // Add type validation if needed based on docKey
      const expectedType = docKey === 'siteImage' ? 'image/' : 'application/pdf';
      const expectedExtension = docKey === 'siteImage' ? '(JPG, PNG, WEBP)' : '(PDF)';
      if (!file.type.startsWith(expectedType) && !(expectedType === 'application/pdf' && file.name.toLowerCase().endsWith('.pdf')) ) {
           // Basic type check (might need refinement for docx etc. if added)
            if (!(docKey !== 'siteImage' && ['application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'].includes(file.type))) {
                 toast.error(`Invalid file type for ${getFriendlyName(docKey)}. Please upload ${expectedExtension}.`);
                 e.target.value = null;
                 return;
            }
      }

      setSelectedFiles((prev) => ({
        ...prev,
        [docKey]: file,
      }));
    }
  };

  const handleRemoveFile = (docKey) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [docKey]: null,
    }));
    const inputElement = document.getElementById(`file-input-${docKey}`);
    if (inputElement) {
      inputElement.value = "";
    }
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      const filesToUpload = {};
      let fileCount = 0;
      Object.keys(selectedFiles).forEach((key) => {
        if (selectedFiles[key]) {
          filesToUpload[key] = selectedFiles[key];
          fileCount++;
        }
      });

      if (fileCount === 0) {
        toast.error("Please select at least one document to upload.");
        setUploading(false);
        return;
      }

      const result = await uploadCompletionDocuments(projectIdFromParams, filesToUpload);

      if (result.success) {
        // Reset selected files locally
        setSelectedFiles({
          specificationReport: null,
          warrantyDocument: null,
          completionCertificate: null,
          siteImage: null,
        });

        // Re-fetch status to update UI visually (important!)
        await checkCompletionDocumentsStatus(projectIdFromParams);

        // *** Navigate back using onBack prop ***
        if (onBack) {
          onBack(); // Navigate back using the function passed via props
        } else {
          navigate("/builder/liveproject"); // Fallback
        }
        // Success toast handled by store
      }
      // Error toasts handled by store
    } catch (error) {
      console.error("Error during document upload submission:", error);
    } finally {
      setUploading(false);
    }
  };

  // Helper to get user-friendly names
  const getFriendlyName = (docKey) => {
    switch (docKey) {
      case "specificationReport": return "Specification Report";
      case "warrantyDocument": return "Warranty Document";
      case "completionCertificate": return "Completion Certificate";
      case "siteImage": return "Site Image";
      default: return docKey;
    }
  };

  const anyFileSelected = Object.values(selectedFiles).some((file) => file !== null);

  // Combined loading state
  const isLoading = isStoreLoading || isCheckingDocs;

  return (
    <div className="flex flex-col items-center justify-center p-2">
      <div className="bg-white shadow-sm rounded-lg p-4 md:p-6 w-full max-w-4xl">
        <h2 className="text-base sm:text-lg font-semibold mb-4 text-center text-gray-700">
          Upload Completion Documents
        </h2>
        <p className="text-center text-xs text-gray-500 mb-6">
            Please upload all required documents to enable the "Completion Request" status change.
        </p>

        {isLoading && !uploading ? ( // Show main loader only if not currently uploading
          <div className="flex justify-center items-center py-10">
            <AiOutlineLoading3Quarters className="animate-spin text-blue-500 text-3xl" />
            <p className="ml-3 text-gray-600">Loading document status...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {apiDocuments.map((doc) => { // Map over fetched document status
              const docKey = docKeyMapping[doc.name];
              const isRequired = doc.required;
              const isUploaded = doc.uploaded; // Use status from API
              const currentFile = selectedFiles[docKey];
              // *** Highlight if required AND not uploaded via API AND no file is selected locally ***
              const needsUploadHighlight = isRequired && !isUploaded && !currentFile;

              return (
                <div
                  key={doc.name} // Use unique name as key
                  className={`bg-gray-50 p-3 rounded-lg shadow-sm text-center transition-all duration-200 border ${
                    needsUploadHighlight // Apply red border if needed
                      ? "border-red-400 ring-1 ring-red-200"
                      : "border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-sm font-medium mb-1 ${
                      needsUploadHighlight ? "text-red-600" : "text-gray-800" // Highlight text color
                    }`}
                  >
                    {doc.name}{" "}
                    {isRequired && <span className="text-red-500">*</span>}
                  </h3>
                  <p className="text-xs text-gray-500 mb-1">Max Size: 5 MB</p>
                  <p className="text-xs text-gray-500 mb-3">
                    Formats:{" "}
                    {doc.name === "Site Image" ? "JPG, PNG, WEBP" : "PDF"} {/* Simplified format list */}
                  </p>

                  {/* Display existing uploaded file OR the file selection input */}
                  {(isUploaded && !currentFile) || currentFile ? ( // If uploaded OR a file is selected
                     <div className={`flex items-center justify-center gap-2 mt-2 px-2 py-1 border rounded text-xs ${
                       currentFile ? 'border-blue-300 bg-blue-50' : 'border-green-300 bg-green-50' // Blue for selected, Green for uploaded
                       }`}>
                       {getFileIcon(currentFile ? currentFile.name : doc.url)}
                       <span className={`truncate flex-1 text-left ${currentFile ? 'text-blue-700' : 'text-green-700'}`}>
                         {currentFile ? currentFile.name : (doc.url ? doc.url.split('/').pop() : 'Uploaded')} {/* Show filename */}
                       </span>
                       {currentFile ? ( // Show remove button for selected file
                         <button
                           onClick={() => handleRemoveFile(docKey)}
                           className="text-xs text-red-500 hover:text-red-700 font-bold p-1"
                           title="Remove selection"
                           disabled={uploading}
                         >
                           ×
                         </button>
                       ) : ( // Show replace button for already uploaded file
                          <label
                            htmlFor={`file-input-${docKey}`}
                            className="cursor-pointer text-blue-500 hover:text-blue-700 text-[10px] font-medium ml-auto whitespace-nowrap"
                            title="Replace Uploaded Document"
                          >
                            Replace
                            <input
                              id={`file-input-${docKey}`}
                              type="file"
                              // Use specific accept types based on docKey
                              accept={docKey === 'siteImage' ? 'image/jpeg,image/png,image/webp' : 'application/pdf'}
                              className="hidden"
                              onChange={(e) => handleFileChange(e, docKey)}
                              disabled={uploading}
                            />
                          </label>
                       )}
                     </div>
                  ) : (
                      // File Input (show if not uploaded and no file selected)
                       <div className="mt-2">
                      <label
                        htmlFor={`file-input-${docKey}`}
                        className={`w-full max-w-[180px] mx-auto cursor-pointer inline-block px-3 py-1.5 border rounded text-xs hover:bg-gray-100 transition-colors ${
                          needsUploadHighlight // Style button based on highlight status
                            ? "border-red-400 text-red-600 bg-red-50 hover:bg-red-100"
                            : "border-gray-300 text-gray-600"
                        }`}
                      >
                        Choose File
                      </label>
                      <input
                        id={`file-input-${docKey}`}
                        type="file"
                        accept={docKey === 'siteImage' ? 'image/jpeg,image/png,image/webp' : 'application/pdf'}
                        className="hidden"
                        onChange={(e) => handleFileChange(e, docKey)}
                        disabled={uploading}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 pt-4 border-t border-gray-200 gap-3">
          {/* Back Button */}
          <button
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-400 transition-colors order-2 sm:order-1 w-full sm:w-auto"
            onClick={onBack || (() => navigate("/builder/liveproject"))} // Use prop or default nav
            disabled={uploading}
          >
            Back to Live Projects {/* Updated text */}
          </button>
          {/* Upload Button */}
          <button
            className={`px-5 py-2 rounded-lg text-white text-sm font-medium flex items-center justify-center order-1 sm:order-2 w-full sm:w-auto ${
              anyFileSelected && !uploading
                ? "bg-blue-500 hover:bg-blue-600"
                : "bg-gray-400 cursor-not-allowed"
            }`}
            onClick={handleSubmit}
            disabled={!anyFileSelected || uploading || isLoading} // Disable if no files or loading/uploading
          >
            {uploading ? (
              <>
                <AiOutlineLoading3Quarters className="animate-spin mr-2 h-4 w-4" />
                Uploading...
              </>
            ) : (
              "Upload Selected"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentUpload;